import React from 'react';
import { Card, CardHeader, CardContent, Box, Typography, IconButton, Avatar, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from '../../../hooks/useTranslation';

export const WidgetActivity = ({ onRemove, isEditMode }) => {
    const t = useTranslation('Dashboard.json');
    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
    };

    const activities = [
        {
            icon: <ShoppingCartIcon />,
            color: '#4CAF50',
            title: t('widget.activity.order.title'),
            description: t('widget.activity.order.description', ['Jean']),
            time: t('widget.activity.order.time')
        },
        {
            icon: <PaymentIcon />,
            color: '#2196F3',
            title: t('widget.activity.payment.title'),
            description: t('widget.activity.payment.description', ['250€', 'Marie Martin']),
            time: t('widget.activity.payment.time')
        },
        {
            icon: <PersonAddIcon />,
            color: '#FF9800',
            title: t('widget.activity.signup.title'),
            description: t('widget.activity.signup.description', ['Sophie']),
            time: t('widget.activity.signup.time')
        }
    ];

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                bgcolor: 'background.paper',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: 'primary.light'
                }
            }}
        >
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#FF980020', width: 32, height: 32 }}>
                            <NotificationsIcon sx={{ fontSize: 18, color: '#FF9800' }} />
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: 'Poppins' }}>
                                Activité récente
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                En temps réel
                            </Typography>
                        </Box>
                    </Box>
                }
                action={
                    isEditMode && (
                        <IconButton
                            className="widget-remove-btn"
                            onClick={handleRemove}
                            onMouseDown={(e) => e.stopPropagation()}
                            size="small"
                            sx={{
                                opacity: 0,
                                pointerEvents: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.main'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )
                }
                sx={{
                    cursor: 'move',
                    pb: 1,
                    bgcolor: 'transparent',
                    '&:hover .widget-remove-btn': {
                        opacity: 1,
                        pointerEvents: 'all'
                    }
                }}
                className="widget-header"
            />
            <CardContent sx={{ flex: 1, pt: 0, overflow: 'auto' }}>
                <List sx={{ p: 0 }}>
                    {activities.map((activity, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                px: 0,
                                py: 1.5,
                                borderRadius: '8px',
                                transition: 'background-color 0.2s',
                                '&:hover': {
                                    bgcolor: 'action.hover'
                                }
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    sx={{
                                        bgcolor: `${activity.color}15`,
                                        color: activity.color,
                                        width: 40,
                                        height: 40
                                    }}
                                >
                                    {React.cloneElement(activity.icon, { sx: { fontSize: 20 } })}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 'clamp(12px, 1.2vw, 13px)',
                                            fontFamily: 'Poppins',
                                            color: 'text.primary'
                                        }}
                                    >
                                        {activity.title}
                                    </Typography>
                                }
                                secondary={
                                    <>
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: 'clamp(11px, 1.1vw, 12px)',
                                                fontFamily: 'Poppins',
                                                color: 'text.secondary',
                                                display: 'block'
                                            }}
                                        >
                                            {activity.description}
                                        </Typography>
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: 'clamp(10px, 1vw, 11px)',
                                                fontFamily: 'Poppins',
                                                color: 'text.disabled',
                                                display: 'block',
                                                mt: 0.25
                                            }}
                                        >
                                            {activity.time}
                                        </Typography>
                                    </>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};
